import { Subscription } from 'rxjs';
import { FieldType, PropertyPreviewMetadata, TypeOfJSON, UpdateMetadata } from '../_services/report-template-design.service';
import { PropertyPreviewComponent } from '../report-template/property-preview/property-preview.component';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';
import { PropOrElemType, TemplateJSON } from './element-type';

export abstract class ReportProperties {
  protected readonly type: PropOrElemType;

  private readonly uuid: string;
  private isExist$: boolean;
  private isSaved$: boolean;

  // https://www.w3.org/TR/css3-values/#absolute-lengths
  private readonly dpi = 96;
  private readonly mmPerInch = 25.4; // 25.4mm = 1inch

  private chainUpdateSubscribers: Array<Subscription>;
  private previewMetadatas: PropertyPreviewMetadata[];
  private originMetadatas: PropertyPreviewMetadata[];

  constructor(type: PropOrElemType,
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    id?: string,
  ) {
    this.langService.prepare(this, this.translate);
    if (id) {
      this.uuid = id;
    } else {
      this.uuid = this.generateUUID();
    }
    this.type = type;
  }

  public getId(): string {
    return this.uuid;
  }

  public getTitle(): string {
    return this.type;
  }

  public getPropOrElemType(): PropOrElemType {
    return this.type;
  }

  public getProperties(): PropertyPreviewMetadata[] {
    return this.previewMetadatas;
  }

  public getMapProperties(): Map<string, PropertyPreviewMetadata[]> {
    const map = new Map<string, PropertyPreviewMetadata[]>();
    this.previewMetadatas.forEach(metadata => {
      if (map.has(metadata.group)) {
        map.get(metadata.group).push(metadata);
      } else {
        const metadataArray: PropertyPreviewMetadata[] = [metadata];
        map.set(metadata.group, metadataArray);
      }
    });
    return map;
  }

  public isSaved(): boolean {
    return this.isSaved$;
  }

  public isExisting(): boolean {
    return this.isExist$;
  }

  public setChainUpdate() {
    if (!this.chainUpdateSubscribers) {
      this.chainUpdateSubscribers = new Array<Subscription>();
    } else if (this.chainUpdateSubscribers && this.chainUpdateSubscribers.length > 0) {
      this.releaseChainUpdate();
    }
    this.setChainUpdateFunction(this.chainUpdateSubscribers);
  }

  public releaseChainUpdate() {
    for (const subscribe of this.chainUpdateSubscribers) {
      subscribe.unsubscribe();
    }
    this.chainUpdateSubscribers = new Array<Subscription>();
  }

  public updateProperties(metadata: UpdateMetadata) {
    this.setUpdateMetadata(metadata);
  }

  protected abstract getTemplateJSON(): TemplateJSON;
  protected abstract getPreviewMetadata(): PropertyPreviewMetadata[];
  protected abstract setChainUpdateFunction(subscribers: Array<Subscription>);
  protected abstract setUpdateMetadata(metadata: UpdateMetadata);

  protected getType(): PropOrElemType {
    return this.type;
  }

  protected setPreviewMetadata() {
    if (!this.previewMetadatas && !this.originMetadatas) {
      this.previewMetadatas = this.getPreviewMetadata();
      this.originMetadatas = this.getPreviewMetadata();
      this.updateMetadataByJSON(this.originMetadatas, this.getTemplateJSON());
      this.updateMetadataByJSON(this.previewMetadatas, this.getTemplateJSON());
    }
  }

  protected setPreviewValue(name: string | string[], value: TypeOfJSON, parentName?: string, childName?: string) {
    let preview: PropertyPreviewMetadata;
    let origin: PropertyPreviewMetadata;
    if ('string' !== typeof name) {
      name = name.join('.');
    }
    for (const metadata of this.previewMetadatas) {
      if (metadata.name === name && (metadata.parentName === null || metadata.parentName === parentName)) {
        preview = metadata;
        break;
      }
    }
    for (const metadata of this.originMetadatas) {
      if (metadata.name === name && (metadata.parentName === null || metadata.parentName === parentName)) {
        origin = metadata;
        break;
      }
    }
    if (preview) {
      preview.value = value;
      preview.isEdited = origin.value !== preview.value;
    }

    this.isSaved$ = true;
    for (const metadata of this.previewMetadatas) {
      if (metadata.isEdited) {
        this.isSaved$ = false;
        break;
      }
    }
  }

  protected setSaveState(value: boolean): void {
    this.isSaved$ = value;
  }

  protected setExistState(value: boolean): void {
    this.isExist$ = value;
  }

  protected fromPixel(px: number, unit: string): number {
    let rtn = px;
    if (px != null) {
      switch (unit) {
        case 'mm':
          rtn = (this.mmPerInch / this.dpi) * px;
          break;
        case 'cm':
          rtn = (this.mmPerInch / 10 / this.dpi) * px;
          break;
        case 'in':
          rtn = px / this.dpi;
          break;
        case 'pt':
          rtn = (px / this.dpi) * 72;
          break;
        default:
          break;
      }
      rtn = this.round(rtn);
    }
    return rtn;
  }

  protected toPixel(val: number, unit: string): number {
    let rtn = val;
    if (val != null) {
      switch (unit) {
        case 'mm':
          rtn = (this.dpi / this.mmPerInch) * val;
          break;
        case 'cm':
          rtn = (this.dpi / this.mmPerInch) * (val / 10);
          break;
        case 'in':
          rtn = val * this.dpi;
          break;
        case 'pt':
          rtn = (val / 72) * this.dpi;
          break;
        default:
          break;
      }
      rtn = this.round(rtn);
    }
    return rtn;
  }

  protected convertUnit(beforeValue: number, fromUnit: string, toUnit: string): number {
    const afterValue = this.toPixel(beforeValue, fromUnit);
    return this.fromPixel(afterValue, toUnit);
  }

  protected round(value: number): number {
    const decimal = 1;
    return Math.round(value * Math.pow(10, decimal)) / Math.pow(10, decimal);
  }

  private updateMetadataByJSON(bases: PropertyPreviewMetadata[], json: TemplateJSON): PropertyPreviewMetadata[] {
    const typeOf = Object.prototype.toString;
    let textFieldType: FieldType;

    for (const base of bases) {
      if (base.name === 'inputType') {
        switch (json[base.name]) {
          case 'text':
          // textFieldType = 'Text';
          // break;
          case 'textArea':
            textFieldType = 'TextArea';
            break;
          case 'select':
          case 'radio':
            break;
        }
      }

      switch (!base.parentName ? typeOf.call(json[base.name]) : null) {
        case '[object Object]':
        case '[object Array]':
          base.value = JSON.stringify(base.parentName ? json[base.parentName][base.name] : json[base.name], undefined, 4);
          break;

        default:
          if (!base.parentName) {
            base.value = json[base.name];
          } else if (base.name === 'options') {
            base.value = JSON.stringify(json[base.parentName][base.name], undefined, 4);
          } else if (base.name === 'title.options') {
            base.value = JSON.stringify(json[base.parentName]['title']['options'], undefined, 4)
          } else if (base.parentName === 'imageInfo' || base.parentName === 'subTotal' || base.parentName === 'title') {

            const nameList = base.name.split('.');
            let result = '';
            if (nameList.length > 1) {
              // name分割時に複数だった場合
              result = json[base.parentName];
              for (let i = 0; i < nameList.length; i++) {
                if (!result) {
                  continue;
                } else {
                  result = result[nameList[i]];
                }
              }
              base.value = result;
            } else if (base.parentName !== 'title') {
              // 既存実装
              base.value = JSON.stringify(json[base.parentName] ? json[base.parentName][base.name] : json[base.name], undefined, 4);
            } else {
              // title用の既存実装
              base.value = json[base.parentName] ? json[base.parentName][base.name] : null;
            }
          } else {
            base.value = json[base.parentName] ? json[base.parentName][base.name] : null;
          }
          break;
      }
    }

    for (const base of bases) {
      if (base.name === 'text' && textFieldType) {
        base.isFieldType = (type: FieldType) => type === textFieldType;
        break;
      }
    }

    return bases;
  }

  protected generateUUID(): string {
    const chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('');
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case 'x':
          chars[i] = Math.floor(Math.random() * 16).toString(16);
          break;
        case 'y':
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
          break;
      }
    }
    return chars.join('');
  }
}
